import { useEffect, useRef, useState } from 'react'
import type { User } from '@hewey/shared'
import { ApiError, api } from '@api'
import { Dialog } from '@components/Dialog.tsx'
import { Field } from '@components/Field.tsx'
import styles from './ChangeEmailDialog.module.css'

// Same two-step as signing in — prove the new inbox, then commit — and the
// same discriminated-union stage shape as SignIn, so the form only ever
// holds the fields the current step needs.
type Stage = { kind: 'email' } | { kind: 'code'; email: string }

type Props = {
	open: boolean
	currentEmail: string
	onClose: () => void
	onChanged: (user: User) => void
}

const messageFor = (e: unknown): string => {
	if (e instanceof ApiError) {
		if (e.code === 'email_taken') {
			return `Another Hewey account already uses that address. If it's yours, sign in to it instead.`
		}
		if (e.code === 'attempts_exhausted') {
			return `Too many wrong attempts. Go back and request a fresh code.`
		}
		return e.message
	}
	return 'Something went wrong'
}

export const ChangeEmailDialog = ({
	open,
	currentEmail,
	onClose,
	onChanged,
}: Props) => {
	const [stage, setStage] = useState<Stage>({ kind: 'email' })
	const [email, setEmail] = useState('')
	const [code, setCode] = useState('')
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const emailRef = useRef<HTMLInputElement>(null)
	const codeRef = useRef<HTMLInputElement>(null)

	// Fresh every time it opens — a half-finished change left over from last
	// time would show a code box for an address you can no longer see.
	useEffect(() => {
		if (!open) return
		setStage({ kind: 'email' })
		setEmail('')
		setCode('')
		setError(null)
		queueMicrotask(() => emailRef.current?.focus())
	}, [open])

	useEffect(() => {
		if (stage.kind === 'code')
			queueMicrotask(() => codeRef.current?.focus())
	}, [stage.kind])

	const normalized = email.trim().toLowerCase()

	const requestCode = async (e: React.FormEvent) => {
		e.preventDefault()
		setBusy(true)
		setError(null)
		try {
			await api.account.email.request({ email: normalized })
			setStage({ kind: 'code', email: normalized })
			setCode('')
		} catch (e) {
			setError(messageFor(e))
		} finally {
			setBusy(false)
		}
	}

	const verifyCode = async (e: React.FormEvent) => {
		e.preventDefault()
		if (stage.kind !== 'code') return
		setBusy(true)
		setError(null)
		try {
			const res = await api.account.email.verify({
				email: stage.email,
				code,
			})
			onChanged(res.user)
			onClose()
		} catch (e) {
			setError(messageFor(e))
		} finally {
			setBusy(false)
		}
	}

	return (
		<Dialog open={open} onClose={onClose}>
			{stage.kind === 'email' ? (
				<form className={styles.panel} onSubmit={requestCode}>
					<h2 className={styles.heading}>Change your email</h2>
					<p className={styles.hint}>
						You sign in as <strong>{currentEmail}</strong>. We'll
						email a 5-digit code to the new address to check it
						reaches you.
					</p>
					<Field label="New email">
						<input
							ref={emailRef}
							type="email"
							required
							autoComplete="email"
							placeholder="you@example.com"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
					</Field>
					{error ? <p className={styles.error}>{error}</p> : null}
					<div className={styles.actions}>
						<button
							type="button"
							className={styles.secondary}
							onClick={onClose}
							disabled={busy}
						>
							Cancel
						</button>
						<button
							type="submit"
							className={styles.primary}
							disabled={busy || normalized.length === 0}
						>
							{busy ? 'Sending…' : 'Send code'}
						</button>
					</div>
				</form>
			) : (
				<form className={styles.panel} onSubmit={verifyCode}>
					<h2 className={styles.heading}>Confirm your new email</h2>
					<p className={styles.hint}>
						We emailed a 5-digit code to{' '}
						<strong>{stage.email}</strong>. Entering it moves your
						account across — you'll stay signed in everywhere.
					</p>
					<Field label="Confirmation code">
						<input
							ref={codeRef}
							type="text"
							required
							inputMode="numeric"
							pattern="\d{5}"
							maxLength={5}
							autoComplete="one-time-code"
							placeholder="·····"
							className={styles.codeInput}
							value={code}
							onChange={(e) =>
								setCode(e.target.value.replace(/\D/g, ''))
							}
						/>
					</Field>
					{error ? <p className={styles.error}>{error}</p> : null}
					<div className={styles.actions}>
						<button
							type="button"
							className={styles.secondary}
							onClick={() => {
								setError(null)
								setStage({ kind: 'email' })
							}}
							disabled={busy}
						>
							Back
						</button>
						<button
							type="submit"
							className={styles.primary}
							disabled={busy || code.length !== 5}
						>
							{busy ? 'Confirming…' : 'Confirm'}
						</button>
					</div>
				</form>
			)}
		</Dialog>
	)
}
